import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Building2, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';

export default function CompanyListPage() {
  const navigate = useNavigate();

  const [companies, setCompanies]   = useState([]);
  const [filtered,  setFiltered]    = useState([]);
  const [query,     setQuery]       = useState('');
  const [loading,   setLoading]     = useState(true);
  const [error,     setError]       = useState(null);
  const [page,      setPage]        = useState(1);

  const PER_PAGE = 50;

  // ── Fetch company list ──────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    axios.get('/stocks')
      .then(res => {
        setCompanies(res.data);
        setFiltered(res.data);
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Client-side filter (fast, no extra requests) ────────────────────────────
  const handleSearch = useCallback((e) => {
    const q = e.target.value;
    setQuery(q);
    setPage(1);
    if (!q.trim()) {
      setFiltered(companies);
    } else {
      const lower = q.toLowerCase();
      setFiltered(
        companies.filter(c =>
          c.nome?.toLowerCase().includes(lower) ||
          c.cnpj?.includes(q) ||
          String(c.cd_cvm)?.includes(q)
        )
      );
    }
  }, [companies]);

  // ── Pagination ──────────────────────────────────────────────────────────────
  const totalPages  = Math.ceil(filtered.length / PER_PAGE);
  const paginated   = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ─────────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64 text-[var(--muted)]">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm font-medium">Carregando empresas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-500 rounded-2xl p-6 text-sm">
        <strong>Erro ao carregar empresas:</strong> {error}
        {error.includes('import_fre') && (
          <p className="mt-2 text-xs text-red-400">
            Execute <code>node backend/server/pipelines/cvm/import_fre.js</code> para popular o banco.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SEO
        title="Empresas Abertas na CVM"
        description="Lista de companhias abertas cadastradas na CVM, com dados do Formulário de Referência (FRE) de 2015 a 2025."
        path="/companies"
      />

      {/* ── Header ── */}
      <div className="border-b border-[var(--border)] pb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-[var(--text)]">
          Empresas Abertas — CVM
        </h1>
        <p className="text-sm text-[var(--muted)] mt-1">
          Dados FRE (Formulário de Referência) de {companies.length.toLocaleString()} companhias
          abertas cadastradas na CVM — 2015 a 2025.
        </p>
      </div>

      {/* ── Search ── */}
      <div className="relative max-w-lg">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)]"
        />
        <input
          type="text"
          value={query}
          onChange={handleSearch}
          placeholder="Buscar por nome, CNPJ ou código CVM..."
          className="w-full pl-10 pr-4 py-2.5 bg-[var(--card)] border border-[var(--border)] rounded-xl text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:border-emerald-500 transition-colors"
        />
        {query && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] text-[var(--muted)] font-medium">
            {filtered.length.toLocaleString()} resultado{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ── Company Grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {paginated.map(company => (
          <button
            key={company.cnpj}
            onClick={() => navigate(`/companies/${company.cd_cvm}`)}
            className="group text-left bg-[var(--card)] border border-[var(--border)] rounded-2xl p-4 hover:border-emerald-500/50 hover:shadow-md hover:shadow-emerald-500/5 transition-all duration-150"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Building2 size={16} className="text-emerald-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--text)] truncate leading-tight">
                    {company.nome}
                  </p>
                  <p className="text-[10px] text-[var(--muted)] mt-0.5 font-mono">
                    {company.cnpj}
                  </p>
                </div>
              </div>
              <ChevronRight
                size={14}
                className="text-[var(--muted)] group-hover:text-emerald-500 flex-shrink-0 mt-0.5 transition-colors"
              />
            </div>

            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[var(--border)]">
              <span className="text-[10px] text-[var(--muted)] font-medium">
                CVM <strong className="text-[var(--text)]">{String(company.cd_cvm).replace(/^0+/, '')}</strong>
              </span>
              <span className="text-[var(--border)]">·</span>
              <span className="text-[10px] text-[var(--muted)] font-medium">
                {company.years_available?.length ?? 0} ano{company.years_available?.length !== 1 ? 's' : ''}
                {company.years_available?.length > 0 && (
                  <span className="ml-1 text-emerald-500">
                    ({Math.min(...company.years_available)}–{Math.max(...company.years_available)})
                  </span>
                )}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-[var(--card)] border border-[var(--border)] text-[var(--muted)] disabled:opacity-40 hover:border-emerald-500/50 transition-all"
          >
            ← Anterior
          </button>
          <span className="text-xs text-[var(--muted)] px-2">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-[var(--card)] border border-[var(--border)] text-[var(--muted)] disabled:opacity-40 hover:border-emerald-500/50 transition-all"
          >
            Próxima →
          </button>
        </div>
      )}

      {paginated.length === 0 && !loading && (
        <div className="text-center py-16 text-[var(--muted)]">
          <Building2 size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">Nenhuma empresa encontrada para "{query}"</p>
        </div>
      )}
    </div>
  );
}
